import { notifications } from '@mantine/notifications';
import { IconCheck, IconX } from '@tabler/icons-react';

interface ShareLinkOptions {
  /** Title passed to the OS share sheet. */
  title: string;
  /** Absolute URL to share. */
  url: string;
  /** Toast message when falling back to a clipboard copy. */
  copyMessage?: string;
}

/**
 * Share a URL the way the platform expects: hand it to the OS share sheet
 * where the browser provides one (navigator.share — mobile browsers, Safari,
 * and some desktop Chromium), and fall back to copying the link to the
 * clipboard elsewhere. Dismissing the share sheet is a user choice, not an
 * error, so it shows nothing.
 */
export const shareLink = async ({
  title,
  url,
  copyMessage = 'Link copied to clipboard',
}: ShareLinkOptions): Promise<void> => {
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, url });
      return;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      // Share sheet unavailable after all (permission policy, unsupported
      // data) — fall through to the clipboard copy.
    }
  }

  try {
    await navigator.clipboard.writeText(url);
    notifications.show({
      title: 'Link copied',
      message: copyMessage,
      color: 'green',
      icon: <IconCheck size={18} />,
      autoClose: 3000,
    });
  } catch {
    notifications.show({
      title: 'Copy failed',
      message: 'Failed to copy link to clipboard',
      color: 'red',
      icon: <IconX size={18} />,
      autoClose: 5000,
    });
  }
};
